import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '../../../core/types';
import { ROUTES } from '../../../core/data/routes';
import { useAppointmentsStore } from '../store/appointmentsStore';
import { ShimmerAnimation, ZeroState, StatusChip } from '../../../core/components/ui';
import { formatDate, formatTime } from '../../../core/utils/formatters';

const UpcomingTab: React.FC = () => {
  const navigate = useNavigate();
  const store = useAppointmentsStore();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSessions = useAppointmentsStore(state => state.fetch);

const load = useCallback(async () => {
  try {
    setLoading(true);

    const result = await fetchSessions();


    setSessions(result || []);
  } catch (error) {
    setSessions([]);
  } finally {
    setLoading(false);
  }
}, [fetchSessions]);
  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <UpcomingSkeleton />;

  if (sessions.length === 0) {
    return (
      <ZeroState
        title="No Upcoming Sessions"
        message="Your booked sessions will appear here. Browse therapists to schedule your first session."
      />
    );
  }

  return (
    <div
    style={{
      padding: '16px 16px 0',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}
  >
      {sessions.map((s, i) => (
        <SessionCard
          key={s.id || i}
          session={s}
          delay={i}
          onTap={() =>
            navigate(ROUTES.UPCOMING_DETAILS, {
              state: { session: s },
            })
          }
        />
      ))}

      <div style={{ height: 16 }} />
    </div>
  );
};

const SessionCard: React.FC<{
  session: Session;
  delay: number;
  onTap: () => void;
}> = ({ session, delay, onTap }) => {
  const initials = (session.therapistName || '?')
    .split(' ')
    .map((n) => n[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="card fade-in-up"
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onTap()}
      style={{
        cursor: 'pointer',
        animationDelay: `${delay * 0.07}s`,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}
      >
        {/* <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--color-primary-container)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
            color: 'var(--color-on-primary-container)',
            flexShrink: 0,
          }}
        >
          {initials}
        </div> */}
<div
  style={{
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'var(--color-primary-container)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  }}
>
  {session.profile_picture_url ? (
    <img
      src={session.profile_picture_url}
      alt={session.therapistName || 'Therapist'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  ) : (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-on-primary-container)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )}
</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: 'var(--color-on-surface)',
              }}
            >
              {session.therapistName || 'Therapist'}
            </div>

            <StatusChip status={session.status || 'PENDING'} />
          </div>

          {session.sessionFrom && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-on-surface-variant)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>

              <span
                style={{
                  fontSize: 12,
                  color: 'var(--color-on-surface-variant)',
                }}
              >
                {formatDate(session.sessionFrom)} ·{' '}
                {formatTime(session.sessionFrom)}
              </span>
            </div>
          )}

          {session.sessionReason && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--color-on-surface-variant)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {session.sessionReason}
            </div>
          )}
        </div>

        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-on-surface-variant)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 4 }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
  );
};

const UpcomingSkeleton: React.FC = () => (
  <div
    style={{
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="card"
        style={{ display: 'flex', gap: 14 }}
      >
        <ShimmerAnimation
          width={44}
          height={44}
          borderRadius="50%"
        />

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <ShimmerAnimation width="50%" height={15} />
          <ShimmerAnimation width="70%" height={12} />
          <ShimmerAnimation width="40%" height={12} />
        </div>
      </div>
    ))}
  </div>
);

export default UpcomingTab;